import React from 'react';
import { useNavigate } from 'react-router-dom';
import Image from 'components/AppImage';
import Button from 'components/ui/Button';
import Icon from 'components/AppIcon';

const ResortsSection = () => {
  const navigate = useNavigate();

  const resorts = [
    {
      id: 1,
      name: "Dandeli Riverside Resort",
      image: "https://images.unsplash.com/photo-1571896349842-33c89424de2d",
      description: "Experience luxury by the river with premium amenities and stunning views",
      rating: 4.8,
      price: "₹2,499",
      features: ["Riverside Location", "Swimming Pool", "Multi-Cuisine Restaurant", "Adventure Sports"],
      marketing: "Most Popular Choice"
    },
    {
      id: 2, 
      name: "Forest Canopy Resort",
      image: "https://images.unsplash.com/photo-1566073771259-6a8506099945",
      description: "Stay amidst the treetops with eco-friendly treehouse accommodations",
      rating: 4.7,
      price: "₹1,999",
      features: ["Treehouse Rooms", "Nature Walks", "Bird Watching", "Organic Meals"],
      marketing: "Eco-Friendly Paradise"
    },
    {
      id: 3,
      name: "Adventure Base Camp",
      image: "https://images.unsplash.com/photo-1551632811-561732d1e306",
      description: "Perfect for thrill-seekers with direct access to all adventure activities",
      rating: 4.9,
      price: "₹1,799",
      features: ["Adventure Hub", "Camping Options", "Guided Tours", "Equipment Rental"],
      marketing: "Adventure Headquarters"
    }
  ];

  const handleViewAllResorts = () => {
    navigate('/resorts-overview');
  };

  const handleResortDetails = (resort) => {
    navigate('/resorts-overview', { state: { selectedResort: resort } });
  };

  const handleBookNow = (resort) => {
    navigate('/booking-engine', { state: { selectedResort: resort } });
  };

  return (
    <section className="py-16 bg-background">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-text-primary mb-4">
            Our Premium Resorts
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto font-body">
            Discover our handpicked collection of luxury resorts in the heart of Dandeli's wilderness
          </p>
        </div>

        {/* Resorts Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {resorts?.map((resort) => (
            <div
              key={resort?.id}
              className="bg-card rounded-2xl shadow-organic overflow-hidden hover-lift group"
            >
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <Image
                  src={resort?.image}
                  alt={resort?.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-organic duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                
                {/* Marketing Badge */}
                <div className="absolute top-4 left-4 bg-accent text-accent-foreground px-3 py-1 rounded-full text-sm font-heading font-semibold">
                  {resort?.marketing}
                </div>

                {/* Rating */}
                <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full flex items-center">
                  <Icon name="Star" size={14} className="text-warning mr-1" />
                  <span className="text-sm font-heading font-semibold text-text-primary">
                    {resort?.rating}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="text-xl font-heading font-semibold text-text-primary mb-2">
                  {resort?.name}
                </h3>
                
                <p className="text-text-secondary mb-4 font-body">
                  {resort?.description}
                </p>

                {/* Features */}
                <div className="mb-4">
                  <div className="grid grid-cols-2 gap-2">
                    {resort?.features?.slice(0, 4)?.map((feature, index) => (
                      <div key={index} className="flex items-center text-sm text-text-secondary">
                        <Icon name="Check" size={12} className="text-primary mr-2 flex-shrink-0" />
                        <span className="font-caption">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Pricing and Actions */}
                <div className="flex items-center justify-between mb-4">
                  <div className="text-left">
                    <span className="text-sm text-text-secondary font-caption">Starting from</span>
                    <div className="text-xl font-heading font-bold text-primary">
                      {resort?.price}
                      <span className="text-sm text-text-secondary font-caption ml-1">/night</span>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="space-y-3">
                  <Button
                    variant="default"
                    size="default"
                    iconName="Calendar"
                    iconPosition="left"
                    fullWidth
                    onClick={() => handleBookNow(resort)}
                    className="hover:scale-105 transition-organic"
                  >
                    Book Now
                  </Button>
                  <Button
                    variant="outline"
                    size="default"
                    iconName="Info"
                    iconPosition="left"
                    fullWidth
                    onClick={() => handleResortDetails(resort)}
                    className="border-secondary text-secondary hover:bg-secondary hover:text-secondary-foreground"
                  >
                    View Details
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* View All Resorts CTA */}
        <div className="text-center mt-12">
          <Button
            variant="outline"
            size="lg"
            iconName="ArrowRight"
            iconPosition="right"
            onClick={handleViewAllResorts}
            className="border-primary text-primary hover:bg-primary hover:text-primary-foreground"
          >
            Explore All Resorts
          </Button>
        </div>
      </div>
    </section>
  );
};

export default ResortsSection;